import { AnySchema, array, object, string } from 'yup'

import { isValidWorkEmail, phoneValidationSchema } from '@/utils/functions/form'
import { getDefaultPhoneCode } from '@/utils/functions/defaultPhoneCode'

// form schema
export type FormValues = {
  // step 0
  first_name: string
  last_name: string
  company: string
  numemployees: string
  email: string
  country: string
  phone: {
    number: string
    extension: string
  }

  // step 1
  est_monthly_content_budget: string

  // step 2
  use_cases: string[]

  // step 3 (conditional)
  content_category: string[]

  // step 4
  requirement_timeline: string

  // step 5 (conditional)
  strategy_solution: string

  // misc
  source?: string
}

export type FormPages =
  | 'getting-started'
  | 'budget'
  | 'use_cases'
  | 'category' // conditional
  | 'requirement_timeline'
  | 'strategy_solution' // conditional

const requiredFieldMessage = 'This field is required'

export const initialValues: FormValues = {
  first_name: '',
  last_name: '',
  company: '',
  numemployees: '',
  email: '',
  country: '',
  phone: {
    number: '',
    extension: getDefaultPhoneCode()
  },
  // step 1
  est_monthly_content_budget: '',
  // step 2
  use_cases: [],
  // step 3 (conditional)
  content_category: [],
  // step 4
  requirement_timeline: '',

  // step 5 (conditional)
  strategy_solution: ''
}

export const validationSchema: { [key in FormPages]: AnySchema } = {
  'getting-started': object().shape({
    first_name: string().required(requiredFieldMessage),
    last_name: string().required(requiredFieldMessage),
    company: string().required(requiredFieldMessage),
    numemployees: string().required(requiredFieldMessage),
    country: string().required(requiredFieldMessage),
    email: string()
      .email('Enter valid email address')
      .test('email', 'Please enter your work email address', value =>
        isValidWorkEmail(value ?? '')
      )
      .required(requiredFieldMessage),
    phone: phoneValidationSchema
  }),
  budget: object().shape({
    est_monthly_content_budget: string().required(requiredFieldMessage)
  }),
  use_cases: object().shape({
    use_cases: array().min(1, 'Please select at least one option')
  }),
  category: object().shape({
    content_category: array().min(1, 'Please select at least one option')
  }),
  requirement_timeline: object().shape({
    requirement_timeline: string().required(requiredFieldMessage)
  }),
  strategy_solution: object().shape({
    strategy_solution: string().required(requiredFieldMessage)
  })
}

export const FORM_STEPS: FormPages[] = [
  'getting-started',
  'budget',
  'use_cases',
  'requirement_timeline'
]

export type CustomForm = {
  firstName: string
  lastName: string
  email: string
  phone: {
    extension: string
    number: string
  }
  company: string
  numEmployees: string
  designation: string
}

export const initialCustomFormValues: CustomForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: {
    extension: getDefaultPhoneCode(),
    number: ''
  },
  company: '',
  numEmployees: '',
  designation: ''
}
export const customFormSchema = object().shape({
  firstName: string()
    .matches(/^\S+$/, 'Space characters are not allowed')
    .max(64, 'First Name cannot be more than 64 characters')
    .required('First name is required'),
  lastName: string()
    .matches(/^\S+$/, 'Space characters are not allowed')
    .max(64, 'Last Name cannot be more than 64 characters')
    .required('Last name is required'),
  email: string()
    .email('Enter valid email address')
    .matches(
      /^[a-zA-Z0-9_.+-]+@(?!gmail|yahoo|outlook|msn|rediff|hotmail|live|ymail|iCloud|protonmail).(.*)$/,
      'Please enter your valid work email'
    )
    .required('Please enter your email address'),
  phone: phoneValidationSchema,
  company: string().required('Please enter company name'),
  numEmployees: string().required('Please select company size'),
  designation: string().required('Please enter your designation')
})
